import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn';

const AUTO_DISMISS_MS = 4000;

/**
 * Brief "welcome back" toast shown to the student each time their
 * tab refocuses after being hidden. Fires on every away→present
 * transition (not just the first) so a student who keeps wandering
 * gets gentle, repeated reinforcement.
 *
 * `triggerKey` is a monotonically-increasing value the parent bumps
 * on each refocus event (e.g. Date.now() at the transition).
 * Same key → no new toast. Reusing one component and bumping the
 * key keeps the animation crisp across rapid leave/return loops.
 */
export default function RefocusToast({ triggerKey }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!triggerKey) return;
    setExiting(false);
    setVisible(true);
    const dismissT = setTimeout(() => setExiting(true), AUTO_DISMISS_MS - 300);
    const hideT = setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, AUTO_DISMISS_MS);
    return () => {
      clearTimeout(dismissT);
      clearTimeout(hideT);
    };
  }, [triggerKey]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        // Lower-middle of the viewport (~72%) so it never overlaps the
        // JoinBanner at center-screen even if both are visible during
        // the join → bail → return loop. translateX centers horizontally.
        'fixed left-1/2 -translate-x-1/2 z-50',
        'top-[72%]',
        'transition-all duration-300 ease-out',
        exiting
          ? 'opacity-0 scale-95'
          : 'opacity-100 scale-100'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3 px-5 py-3',
          'bg-gray-900 text-white rounded-2xl',
          'dark:bg-white dark:text-gray-900',
          'border border-gray-700/40 dark:border-gray-300',
          'shadow-2xl'
        )}
      >
        <div
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            'bg-emerald-500/20 dark:bg-emerald-500/15'
          )}
        >
          <Sparkles className="w-4 h-4 opacity-90" strokeWidth={2} />
        </div>
        <p className="text-sm font-medium">
          Welcome back — try to stay here so your teacher can help you.
        </p>
      </div>
    </div>
  );
}
